// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PdpProductPriceAndActions, {
    PdpProductPriceAndActionsStyle,
} from "@rpsc/App/Components/Product/PdpProductPriceAndActions";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@rpsc/Overrides/Components/ProductPartNumbers";
import React, { FC } from "react";

type Props = HasProductContext;

export interface ProductDetailsSummaryStyles {
    container?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    productImage?: LazyImageProps;
    productTitle?: TypographyPresentationProps;
    brandStyles?: ProductBrandStyles;
    titleWrapper?: InjectableCss;
    actionStyles?: PdpProductPriceAndActionsStyle;
    brandAndPartNumbers?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
}

export const productDetailsSummaryStyles: ProductDetailsSummaryStyles = {};

const styles = productDetailsSummaryStyles;

const ProductDetailsSummary: FC<Props> = ({ productContext }) => {
    const { product } = productContext;

    if (!product) {
        return null;
    }

    const productImage = product.smallImagePath || product.mediumImagePath || product.largeImagePath;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftSection}>
                <LazyImage
                    src={productImage}
                    altText={product.imageAltText || product.productTitle}
                    {...styles.productImage}
                />
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.productTitle}>{product.productTitle}</Typography>
                    <StyledWrapper {...styles.brandAndPartNumbers}>
                        {product.brand && (
                            <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                        )}
                        <ProductPartNumbers
                            manufacturerItem={product.manufacturerItem}
                            productNumber={product.productNumber}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <PdpProductPriceAndActions extendedStyles={styles.actionStyles} showVariants={false} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductDetailsSummary);
